import React, { useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { FiCurrency } from '../types';

type StripeElementsProviderProps = {
  children: React.ReactNode;
  currency?: FiCurrency;
} & ({ mode: 'payment'; amountInCents: number } | { mode: 'setup' });

/**
 * Wraps children in stripe's Elements provider for use with PaymentElement, AddressElement,
 * and LinkAuthenticationElement. Supports both payment mode (immediate charge) and setup mode
 * (save payment method for future use).
 */
export default function StripeElementsProvider(props: StripeElementsProviderProps) {
  const { children, currency = FiCurrency.USD, mode } = props;
  const amount = mode === 'payment' ? props.amountInCents : undefined;

  /**
   * Memoized so the stripe instance is created once per component lifetime. Uses recurly's
   * publishable key + fi's stripe connect account id per recurly's third-party checkout docs.
   */
  const stripePromise = useMemo(() => {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    const accountId = process.env.REACT_APP_STRIPE_ACCOUNT_ID;
    if (!key || !accountId) {
      console.warn(
        'REACT_APP_STRIPE_PUBLISHABLE_KEY or REACT_APP_STRIPE_ACCOUNT_ID is not set — stripe elements will not load',
      );
      return null;
    }
    return loadStripe(key, { stripeAccount: accountId });
  }, []);

  const options: StripeElementsOptions = useMemo(() => {
    const base = {
      currency: currency.toLowerCase(),
      loader: 'auto' as const,
      paymentMethodCreation: 'manual' as const,
    };

    if (mode === 'payment' && amount !== undefined) {
      return {
        ...base,
        mode: 'payment' as const,
        amount,
        setupFutureUsage: 'off_session' as const,
      };
    }

    return {
      ...base,
      mode: 'setup' as const,
      setupFutureUsage: 'off_session' as const,
    };
  }, [mode, amount, currency]);

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
}
